import { i18n } from '@/localization/i18n';
import { blockOfflineAction, logger } from '@/shared/utils';
import { store } from '@/app/store';
import { setGroupParticipants } from '@/features/chats/model/groupParticipants/reducer';
import { formatUserIdToJid } from '@/features/chats/utils';
import { makeAsAdmin, removeAdmin, removeParticipant, userExitGroup } from '@/infrastructure/sdk';
import { showErrorToast, showSuccessToast } from '@/shared/utils/toastMessage';

/**
 * Dispatches SDK group-member actions and updates Redux optimistically.
 */
export const handleGroupMemberAction = async ({ groupId, userId, action, displayName }) => {
   if (blockOfflineAction()) return false;
   const groupJid = formatUserIdToJid(groupId, 'groupchat');
   const userJid = formatUserIdToJid(userId);

   try {
      let res;
      switch (action) {
         case 'exitGroup':
            res = await userExitGroup(groupJid, userJid);
            break;
         case 'makeAdmin':
            res = await makeAsAdmin(groupJid, userJid);
            break;
         case 'removeAdmin':
            res = await removeAdmin(groupJid, userJid);
            break;
         case 'removeMember':
            res = await removeParticipant(groupJid, userJid);
            break;
         default:
            return;
      }

      if (res?.statusCode === 200) {
         const currentParticipants = store.getState().groupParticipantsData.participants?.[groupId];

         const actionConfig = {
            exitGroup: {
               update: list => list.filter(p => p.userId !== userId),
               toast: i18n.t('toast.EXIT_GROUP_SUCCESS'),
            },
            removeMember: {
               update: list => list.filter(p => p.userId !== userId),
               toast: i18n.t('toast.MEMBER_REMOVED_SUCCESS', { displayName }),
            },
            makeAdmin: {
               update: list => list.map(p => (p.userId === userId ? { ...p, userType: 'o' } : p)),
               toast: i18n.t('toast.MEMBER_MADE_ADMIN_SUCCESS', { displayName }),
            },
            removeAdmin: {
               update: list => list.map(p => (p.userId === userId ? { ...p, userType: 'n' } : p)),
               toast: i18n.t('toast.MEMBER_REMOVED_ADMIN_SUCCESS', { displayName }),
            },
         };

         const { update, toast } = actionConfig[action];
         store.dispatch(setGroupParticipants({ groupId, participants: update(currentParticipants) }));
         showSuccessToast(toast);
         return true;
      } else {
         showErrorToast(res?.message || i18n.t('toast.ACTION_FAILED'));
         return false;
      }
   } catch (err) {
      logger.error(`handleGroupMemberAction [${action}] failed:`, err);
      showErrorToast(err?.message || i18n.t('toast.SOMETHING_WENT_WRONG_RETRY'));
      return false;
   }
};
